import { analyzeFiles, ImportTransfer, libraryApi } from "./importer.js";
import { normalizeCloud } from "./player.js";
const $ = (id) => document.getElementById(id);
const esc = (value) =>
  String(value ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ],
  );
const mib = (n) => (n / 1048576).toFixed(1) + " MiB";
function playlistId(input) {
  const value = String(input || "").trim();
  if (/^[1-9][0-9]{0,17}$/.test(value)) return value;
  try {
    const url = new URL(value);
    if (!/^(?:music|m\.music)\.163\.com$/i.test(url.hostname)) return "";
    const id = url.searchParams.get("id") || new URLSearchParams(url.hash.split("?")[1] || "").get("id");
    return /^[1-9][0-9]{0,17}$/.test(id || "") ? id : "";
  } catch { return ""; }
}
const uploadGuide = `
  <details class="import-guide" open>
    <summary>上传说明与识别规则</summary>
    <div class="import-guide-body">
      <section><h3>1 · 准备文件</h3><p>建议一张专辑放在一个文件夹，曲名以 01、02… 开头；多碟可放入 CD1、CD2 子文件夹。支持 MP3、FLAC、M4A、AAC、OGG、WAV；封面支持 PNG、JPEG、WebP；歌词支持 LRC。手机不能选文件夹时，可点“选择多个文件”。</p></section>
      <section><h3>2 · 自动识别</h3><p>优先读取音频标签，再读取 mp3tag.txt、meta.toml / CUE 和文件名；按碟号、曲号排列。同一专辑中每首歌的歌手可不同，缺少统一专辑歌手时会归为“群星”；“器乐版”文件夹单独列为一张专辑。封面从 cover、folder、封面图片或音频内嵌图中选择。歌词按曲号和曲名匹配，逐行与逐字版本可同时保留。若 CUE 指向整轨音频且已有分轨，整轨会被跳过，避免重复歌曲；只有整轨时不会自动切割。</p></section>
      <section><h3>3 · 检查并上传</h3><p>选完文件后先核对专辑名、歌手、曲序、封面和歌词提示；可直接修改预览中的显示信息，原文件不会改名。确认后上传到服务器。FLAC 原件会保留，网站另备 MP3 320 kbps 播放版供浏览器使用。单首音频最大 200 MiB、封面 15 MiB、歌词 2 MiB；每批最多 500 首、总计 2 GiB。ZIP、说明文档和未匹配的附件不会上传。</p></section>
      <section><h3>4 · 权限和续传</h3><p>需先注册本站账号并获得管理员的“上传与维护”授权。上传中可以暂停；刷新或关闭页面后，重新选择完全相同的文件夹和预览内容即可续传。相同批次再次提交不会重复入库；要替换已上传的音频或封面，请重新导入。</p></section>
    </div>
  </details>`;
export class LibraryUI {
  constructor(hooks) {
    this.hooks = hooks;
    this.catalog = { albums: [], tracks: [] };
    this.view = "albums";
    this.preview = null;
    this.busy = false;
    this.progress = null;
    this.account = null;
    this.authMode = "login";
    this.returnTo = "library";
    this.activeAlbum = null;
    this.neteaseTracks = [];
    this.neteasePlaylists = [];
    this.neteasePage = 0;
    this.neteaseTotal = 0;
    this.neteasePlaylistId = "";
    this.neteaseRequest = 0;
    this.qrToken = 0;
    document.addEventListener("click", (event) => {
      const el = event.target.closest("[data-library]");
      if (el && !el.disabled)
        this.action(el).catch((e) => hooks.toast(e.message));
    });
    document.addEventListener("submit", (event) => {
      if (["libraryLogin", "libraryRegister", "ownerClaim", "albumEdit", "trackEdit", "neteasePublicImport", "cloudCreate"].includes(event.target.id)) {
        event.preventDefault();
        this.submit(event.target).catch((e) => hooks.toast(e.message));
      }
    });
    document.addEventListener("change", (event) => {
      const el = event.target;
      if (el.dataset.importField && this.preview) {
        const track = this.preview.tracks[Number(el.dataset.index)];
        track[el.dataset.importField] = ["number", "disc"].includes(
          el.dataset.importField,
        )
          ? Number(el.value)
          : el.value;
      }
      if (el.dataset.albumField && this.preview) {
        const old = el.dataset.group;
        for (const track of this.preview.tracks)
          if (track.album === old) track[el.dataset.albumField] = el.value;
        this.renderImport();
      }
    });
  }
  async refresh() {
    this.catalog = await libraryApi("/catalog");
    return this.catalog;
  }
  async show() {
    this.hooks.showDialog("library");
    $("dialogPanel").innerHTML =
      this.hooks.panelHeader("服务器曲库") +
      '<p class="list-message">正在读取专辑…</p>';
    try {
      this.account = (await libraryApi("/session")).account;
      if (!this.account?.canListen) {
        this.renderAccount();
        return;
      }
      await this.refresh();
      this.render();
    } catch (e) {
      if ([401, 403].includes(e.status)) this.renderAccount();
      else $("dialogPanel").innerHTML =
        this.hooks.panelHeader("服务器曲库") + `<p class="list-message">${esc(e.message)}</p>`;
    }
  }
  render() {
    const all = this.hooks.allTracks();
    const albums = this.catalog.albums;
    let content = "";
    if (this.view === "albums")
      content = albums.length
        ? '<div class="album-grid">' +
          albums
            .map(
              (a) =>
                `<button class="album-tile" data-library="album" data-id="${a.id}">${a.cover ? `<img src="${esc(a.cover)}" alt="" loading="lazy">` : '<span class="album-placeholder">R</span>'}<strong>${esc(a.title)}</strong><small>${esc(a.artist)} / ${a.tracks.length} 首${a.year ? " / " + esc(a.year) : ""}</small></button>`,
            )
            .join("") +
          "</div>"
        : '<p class="list-message">还没有导入专辑。选择“导入音乐”，可以把整张专辑上传到服务器。</p>';
    if (this.view === "artists") {
      const artists = [...new Set(all.map((t) => t.artist))].sort((a, b) =>
        a.localeCompare(b, "zh-CN"),
      );
      content =
        '<div class="library-groups">' +
        artists
          .map(
            (a) =>
              `<button data-library="artist" data-value="${esc(a)}"><span>${esc(a)}</span><small>${all.filter((t) => t.artist === a).length} 首 →</small></button>`,
          )
          .join("") +
        "</div>";
    }
    if (this.view === "folders") {
      const folders = [
        ...new Set(
          this.catalog.tracks.map((t) =>
            t.relativePath.includes("/")
              ? t.relativePath.slice(0, t.relativePath.lastIndexOf("/"))
              : "根目录",
          ),
        ),
      ].sort();
      content = folders.length
        ? '<div class="library-groups">' +
          folders
            .map(
              (a) =>
                `<button data-library="folder" data-value="${esc(a)}"><span>${esc(a)}</span><small>${this.catalog.tracks.filter((t) => directoryOf(t.relativePath) === a).length} 首 →</small></button>`,
            )
            .join("") +
          "</div>"
        : '<p class="list-message">导入音乐后，会保留原来的文件夹层级。</p>';
    }
    if (this.view === "imports") content = this.renderImports();
    if (this.view === "songs") content = this.rows(all);
    $("dialogPanel").innerHTML =
      this.hooks.panelHeader(
        "服务器曲库",
        `${albums.length} ALBUMS / ${all.length} TRACKS`,
      ) +
      `<div class="library-toolbar"><button class="solid-button" data-action="browser-playlists">浏览器歌单</button>${this.account?.canUpload ? '<button class="solid-button primary" data-library="import">＋ 导入音乐</button>' : ''}<button class="solid-button" data-library="refresh">刷新 ↻</button><button class="solid-button" data-library="netease">网易云我的音乐 ↗</button></div><div class="library-tabs" role="tablist" aria-label="曲库分类">${[
        ["albums", "专辑"],
        ["artists", "歌手"],
        ["folders", "文件夹"],
        ["songs", "全部歌曲"],
        ...(this.account?.canUpload ? [["imports", "导入历史"]] : []),
      ]
        .map(
          ([id, label]) =>
            `<button role="tab" aria-selected="${this.view === id}" data-library="view" data-value="${id}">${label}</button>`,
        )
        .join("")}</div>${content}`;
  }
  rows(tracks) {
    return (
      '<ol class="song-list">' +
      tracks
        .map(
          (t) =>
            `<li class="song-row"><span class="index">${String(t.trackNumber || tracks.indexOf(t) + 1).padStart(2, "0")}</span><button class="song-main" aria-label="播放 ${esc(t.title)}" data-library="play-track" data-key="${esc(t.key)}"><strong>${esc(t.title)}</strong><small>${esc(t.artist)} / ${esc(t.album)}</small></button>${this.account?.canUpload && t.key?.startsWith("library:") ? `<button class="row-action" data-library="edit-track" data-id="${esc(t.id)}" aria-label="编辑 ${esc(t.title)} 的曲目信息">编辑</button><button class="row-action danger" data-library="delete-track" data-id="${esc(t.id)}" data-title="${esc(t.title)}" aria-label="删除 ${esc(t.title)}">删除</button>` : ""}<button class="row-action" data-library="detail-track" data-key="${esc(t.key)}" aria-label="查看 ${esc(t.title)} 的详情">↗</button></li>`,
        )
        .join("") +
      "</ol>"
    );
  }
  async loadImports() {
    try {
      const data = await libraryApi("/imports");
      this.imports = data.imports || [];
    } catch (e) {
      this.imports = [];
      this.hooks.toast(e.message);
    }
  }
  renderImports() {
    if (!this.imports) {
      this.loadImports();
      return '<p class="list-message">正在读取导入历史…</p>';
    }
    if (!this.imports.length)
      return '<p class="list-message">暂无导入记录。</p>';
    const stateLabel = {
      uploading: "上传中",
      published: "已发布",
    };
    return (
      '<ol class="song-list">' +
      this.imports
        .map((imp) => {
          const date = new Date(imp.createdAt).toLocaleString();
          const info = imp.result
            ? `${imp.result.albums || 0} 张专辑 · ${imp.result.tracks || 0} 首`
            : "";
          return `<li class="song-row"><button class="song-main"><strong>导入 ${imp.id.slice(0, 8)}</strong><small>${stateLabel[imp.state] || imp.state}${info ? " / " + info : ""} · ${date}</small></button>${imp.state === "published" ? `<button class="row-action danger" data-library="delete-import" data-id="${esc(imp.id)}" aria-label="删除此导入产生的全部专辑">删除</button>` : ""}</li>`;
        })
        .join("") +
      "</ol>"
    );
  }
  album(id) {
    const album = this.catalog.albums.find((a) => a.id === id);
    if (!album) return;
    this.activeAlbum = album;
    this.hooks.useCollection(album.tracks, "local", album.title);
    this.hooks.showDialog("album");
    $("dialogPanel").innerHTML =
      this.hooks.panelHeader("专辑详情") +
      `<button class="text-back" data-library="catalog">← 返回我的音乐</button><div class="album-heading">${album.cover ? `<img src="${esc(album.cover)}" alt="专辑封面">` : ""}<div><h3>${esc(album.title)}</h3><p>${esc(album.artist)}</p><small>${esc(album.year)} / ${album.tracks.length} 首</small></div></div><div class="library-toolbar"><button class="solid-button primary" data-library="play-album">▶ 播放全部</button><button class="solid-button" data-library="browse-album">查看 3D 资料库 ↗</button>${this.account?.canUpload ? '<button class="solid-button" data-library="edit-album">编辑专辑</button><button class="solid-button danger" data-library="delete-album" data-id="' + album.id + '" data-title="' + esc(album.title) + '">删除专辑</button>' : ''}</div>${this.rows(album.tracks)}`;
  }
  async openImport() {
    this.hooks.showDialog("import");
    try {
      this.account = (await libraryApi("/session")).account;
    } catch (e) {
      $("dialogPanel").innerHTML =
        this.hooks.panelHeader("导入音乐") +
        `<p class="list-message">${esc(e.message)}</p>`;
      return;
    }
    this.renderImport();
  }
  renderImport() {
    if (this.hooks.currentDialog() !== "import") return;
    if (!this.account?.canUpload) {
      this.returnTo = "import";
      this.renderAccount();
      return;
    }
    let content = "";
    if (this.busy)
      content =
        '<p class="list-message" role="status">' +
        esc(this.analysisMessage || "正在识别文件…") +
        "</p>";
    else if (this.progress?.done) {
      content = `<div class="import-success"><strong>导入完成</strong><p>${this.progress.already ? "这批文件已经导入，无需重复上传。" : `已保存 ${this.progress.result.albums} 张专辑、${this.progress.result.tracks} 首歌曲。`}</p><button class="solid-button primary" data-library="catalog">查看我的音乐 →</button><button class="solid-button" data-library="reset-import">继续导入</button></div>`;
    } else if (this.transfer && this.progress) {
      const p = this.progress;
      const percentage = p.total
        ? Math.min(100, (p.uploaded / p.total) * 100)
        : 0;
      content = `<section class="import-progress" aria-label="上传进度"><strong>${p.paused ? "导入已暂停" : "正在上传到服务器"}</strong><progress max="100" value="${percentage}"></progress><p>${percentage.toFixed(1)}% · ${mib(p.uploaded || 0)} / ${mib(p.total || this.preview.totalBytes)}</p><p>${p.completed || 0} / ${p.files || this.preview.files.length} 个文件</p><p class="upload-filename">${esc(p.name || "")}</p>${p.error ? `<p class="import-error" role="alert">${esc(p.error)}</p>` : ""}<button class="solid-button primary" data-library="${p.paused ? "resume" : "pause"}">${p.paused ? "继续上传" : "暂停上传"}</button><p class="setting-description">关闭页面后，重新选择同一个文件夹并保持预览内容一致，可以继续这次上传。</p></section>`;
    } else if (this.preview) {
      const p = this.preview;
      const albums = [...new Set(p.tracks.map((t) => t.album))];
      content = `<p class="import-summary">${albums.length} 张专辑 · ${p.tracks.length} 首歌曲 · ${p.files.length} 个文件 · ${mib(p.totalBytes)}</p><p class="setting-description">已忽略 ${p.ignored} 个未使用附件或压缩包。请核对封面、曲序和歌词；以下显示信息可修改，原文件不改名。</p>${p.notices.length ? `<details class="import-notices" open><summary>识别说明 / 名称差异 ${p.notices.length} 项</summary>${p.notices.map((n) => `<p>${esc(n)}</p>`).join("")}</details>` : ""}<div class="import-albums">${albums
        .map((album) => {
          const t = p.tracks.find((t) => t.album === album);
          const f = p.files.find((e) => e.path === t.cover);
          let thumb = "";
          if (f) {
            this.objectUrls ||= new Map();
            if (!this.objectUrls.has(t.cover))
              this.objectUrls.set(t.cover, URL.createObjectURL(f.file));
            thumb = this.objectUrls.get(t.cover);
          }
          return `<div class="import-album">${thumb ? `<img src="${thumb}" alt="待导入的专辑封面">` : ""}<label>专辑名称<input data-album-field="album" data-group="${esc(album)}" value="${esc(album)}"></label><label>专辑艺术家<input data-album-field="albumArtist" data-group="${esc(album)}" value="${esc(t.albumArtist)}"></label></div>`;
        })
        .join(
          "",
        )}</div><div class="import-track-list">${p.tracks.map((t, i) => `<div class="import-track"><label class="track-number">曲号<input type="number" min="1" max="9999" data-import-field="number" data-index="${i}" value="${t.number}"></label><label>曲名<input data-import-field="title" data-index="${i}" value="${esc(t.title)}"></label><label>歌手<input data-import-field="artist" data-index="${i}" value="${esc(t.artist)}"></label><small>${t.lineLyric ? "逐行 ✓" : "逐行 —"} / ${t.wordLyric ? "逐字 ✓" : "逐字 —"} / 碟 ${t.disc}</small></div>`).join("")}</div><div class="import-bottom"><button class="solid-button primary" data-library="start-import">确认并上传 ${p.tracks.length} 首歌曲 →</button><button class="solid-button" data-library="reset-import">重新选择</button></div>`;
    } else
      content = `<div class="import-picker"><p>把整张专辑保存到你的音乐服务器。</p><button class="solid-button primary" data-library="pick-folder">选择整个文件夹</button><button class="solid-button" data-library="pick-files">选择多个文件</button>${uploadGuide}</div>`;
    $("dialogPanel").innerHTML =
      this.hooks.panelHeader("导入音乐", "IMPORT / SERVER LIBRARY") +
      content +
      `<div class="manager-footer"><span>本站账号 ${esc(this.account.qq)}</span><button data-library="account">账号设置</button></div>`;
  }
  renderAccount() {
    this.hooks.showDialog("account");
    const user = this.account;
    let content;
    if (user) {
      const roles = [user.admin ? "管理员" : null, user.canListen ? "可听服务器专辑" : "待审核", user.canUpload ? "可上传与维护" : null].filter(Boolean);
      content = `<div class="account-shell">
        <section class="account-identity" aria-label="当前账号"><span class="account-kicker">当前登录</span><strong>QQ ${esc(user.qq)}</strong><div class="account-badges">${roles.map((role) => `<span>${role}</span>`).join("")}</div></section>
        ${!user.canListen ? '<p class="account-status" role="status">注册已完成。管理员开通权限后，刷新页面即可听服务器专辑。</p>' : ''}
        <section class="account-section" aria-label="音乐服务"><span class="account-kicker">音乐服务</span><button class="account-link" data-library="netease"><span><strong>网易云账号与歌单</strong><small>扫码绑定、同步喜欢与歌单</small></span><span aria-hidden="true">↗</span></button></section>
        ${user.admin ? '<section class="account-section" aria-label="账号管理"><span class="account-kicker">管理入口</span><button class="account-link" data-library="users"><span><strong>账号授权管理</strong><small>审核听歌、上传与曲目信息维护权限</small></span><span aria-hidden="true">↗</span></button></section>' : ''}
        <div class="account-bottom"><p>QQ 号仅作为本站账号名，不代表已验证 QQ 号归属。请勿填写 QQ 平台密码。</p><button class="solid-button" data-library="logout">退出本站账号</button></div>
        ${!user.admin ? '<details class="owner-claim"><summary>首次部署：认领管理员账号</summary><p class="setting-description">仅首次初始化需要填写旧的管理密码；认领后日常登录只用本站账号。</p><form id="ownerClaim" class="manager-form"><label>初始化凭证<input name="bootstrapPassword" type="password" autocomplete="off" required maxlength="200"></label><button class="solid-button" type="submit">认领管理员</button><p id="loginError" role="alert"></p></form></details>' : ''}
      </div>`;
    } else {
      const register = this.authMode === "register";
      content = `<div class="account-shell account-auth">
        <div class="account-intro"><span class="account-kicker">${register ? "创建本站账号" : "欢迎回来"}</span><h3>${register ? "注册账号" : "登录账号"}</h3><p>${register ? "注册后由管理员开通听歌和上传权限。" : "登录后查看你的专辑权限与网易云歌单。"}</p></div>
        <form id="${register ? "libraryRegister" : "libraryLogin"}" class="manager-form account-form"><label>QQ 号<input name="qq" inputmode="numeric" pattern="[1-9][0-9]{4,11}" autocomplete="username" placeholder="填写 QQ 号" required maxlength="12"></label><label>本站密码<input name="password" type="password" autocomplete="${register ? "new-password" : "current-password"}" placeholder="${register ? "至少 10 位，使用独立密码" : "填写本站密码"}" required minlength="${register ? 10 : 1}" maxlength="128"></label><button class="solid-button primary" type="submit">${register ? "创建账号" : "登录本站"} <span aria-hidden="true">→</span></button><p id="loginError" role="alert"></p></form>
        <button class="account-switch" data-library="auth-mode" data-value="${register ? "login" : "register"}">${register ? "已有账号？返回登录" : "没有账号？注册一个"} <span aria-hidden="true">→</span></button>
        <p class="account-note">QQ 号仅作本站账号名，不代表已验证归属。本站密码与 QQ 平台密码不同，请勿在这里填写 QQ 平台密码。</p>
      </div>`;
    }
    $("dialogPanel").innerHTML = this.hooks.panelHeader("本站账号", "SETTINGS / ACCOUNT") + '<button class="text-back" data-action="settings">← 返回设置</button>' + content;
  }
  async renderNetease() {
    const request=++this.neteaseRequest;
    this.qrToken++;
    this.hooks.showDialog("netease");
    $("dialogPanel").innerHTML = this.hooks.panelHeader("我的音乐 · 网易云") + '<p class="list-message">正在读取账号…</p>';
    let account, playlists;
    try {
      ({account} = await libraryApi("/netease/account"));
      ({playlists} = await libraryApi("/netease/playlists"));
    } catch(error) {
      if(request!==this.neteaseRequest||this.hooks.currentDialog()!=="netease") return;
      $("dialogPanel").innerHTML=this.hooks.panelHeader("我的音乐 · 网易云","NETEASE / MY MUSIC")+`<p class="list-message">${esc(error.message)}</p><button data-library="account">前往设置登录本站账号 ↗</button><button class="text-back" data-action="library">服务器曲库 ↗</button>`;
      return;
    }
    if(request!==this.neteaseRequest||this.hooks.currentDialog()!=="netease") return;

    this.neteasePlaylists = playlists;
    try { this.catalog = await libraryApi("/catalog"); this.cloudServer = this.catalog.albums || []; }
    catch { this.cloudServer = []; }
    if(request!==this.neteaseRequest||this.hooks.currentDialog()!=="netease") return;
    const publicForm = '<form id="neteasePublicImport" class="manager-form"><label>公开歌单链接或编号<input name="playlist" required placeholder="https://music.163.com/playlist?id=..."></label><button class="solid-button" type="submit">导入公开歌单</button><p id="neteaseImportError" role="alert"></p></form>';
    const listHtml = `<div class="library-groups">${playlists.map((p) => `<button data-library="netease-list" data-id="${esc(p.id)}"><span>${p.liked ? "♥ " : ""}${esc(p.name)}</span><small>${p.liked ? "我喜欢的音乐" : p.public ? "公开歌单" : p.created ? "创建" : "收藏"} · ${p.trackCount} 首 →</small></button>`).join("") || '<p class="list-message">暂无歌单，绑定后同步或导入公开歌单。</p>'}</div>`;
    if (!account && !this.cloudServer.length) {
      $("dialogPanel").innerHTML = this.hooks.panelHeader("我的音乐 · 网易云", "ACCOUNT / BIND") +
        '<button class="text-back" data-action="library">服务器曲库 ↗</button><p class="setting-description">使用网易云音乐 App 扫码绑定。不需要在本站填写网易云密码。歌单仅对当前本站账号可见；歌曲仍受网易云版权和会员权限限制。</p><button class="solid-button primary" data-library="netease-qr">扫码绑定网易云</button><p class="setting-description">暂时不绑定也可导入公开歌单；无法读取你的私有歌单或喜欢。</p>' + publicForm + listHtml;
      return;
    }
    this.cloudAccount=account;
    this.cloudTab=account?(this.cloudTab||"liked"):"server";
    this.cloudQuery="";
    this.cloudManagement=publicForm;
    await this.drawCloudHome(true);
  }
  async drawCloudHome(load=false) {
    const tab=this.cloudTab||"liked", token=++this.neteaseRequest;
    this.hooks.showDialog("netease");
    this.cloudError = '';
    if(load && tab==='albums') {
      this.cloudAlbums=[]; this.cloudAlbumOffset=0; this.cloudAlbumMore=false;
      try { const data=await libraryApi('/netease/albums?offset=0');
        if(token!==this.neteaseRequest||this.hooks.currentDialog()!=="netease")return;
        this.cloudAlbums=data.albums; this.cloudAlbumOffset=data.albums.length; this.cloudAlbumMore=data.more;
      } catch(e) { if(token!==this.neteaseRequest)return; this.cloudError=e.message; }
    }
    if(load && tab==='liked') {
      const liked=this.neteasePlaylists.find(p=>p.liked);
      this.cloudSongs=[]; this.cloudOffset=0; this.cloudTotal=0; this.cloudError='';
      this.drawCloudDirectory();
      if(liked) {
        try {
          const data=await libraryApi('/netease/playlists/'+encodeURIComponent(liked.id)+'/tracks?offset=0&limit=100');
          if(token!==this.neteaseRequest||this.hooks.currentDialog()!=="netease")return;
          this.cloudSongs=data.tracks.filter(Boolean).map(normalizeCloud);this.cloudOffset=data.tracks.length;this.cloudTotal=data.total;
        } catch(e) {if(token!==this.neteaseRequest)return;this.cloudError=e.message;}
      }
    }
    if(token===this.neteaseRequest&&this.hooks.currentDialog()==='netease')this.drawCloudDirectory();
  }
  drawCloudDirectory() {
    const tab=this.cloudTab||'liked',query=(this.cloudQuery||'').toLocaleLowerCase();
    let rows=tab==='server'?(this.cloudServer||[]):tab==='albums'?(this.cloudAlbums||[]):tab==='liked'?(this.cloudSongs||[]):this.neteasePlaylists.filter(p=>tab==='created'?(p.created&&!p.liked):(!p.created&&!p.liked));
    rows=rows.filter(p=>[p.title||p.name,p.artist,p.album].join(' ').toLocaleLowerCase().includes(query));
    if(tab==='liked'&&this.cloudSort)rows=[...rows].sort((a,b)=>(a[this.cloudSort]||'').localeCompare(b[this.cloudSort]||''));
    this.cloudVisible=rows;
    $("dialogPanel").innerHTML=`<div class="cloud-directory-header"><small>RHINE LAB / MUSIC DATABASE / MUSIC DIRECTORY</small><button data-action="close-dialog">← 返回</button><h2>MY MUSIC</h2><p>我的音乐 / ${esc(this.cloudAccount?.nickname||'服务器曲库')}</p></div>
      <div class="cloud-query"><label><small>QUERY / 检索词</small><input id="cloudDirectoryQuery" value="${esc(this.cloudQuery||'')}" placeholder="搜索已载入的歌曲、歌手、专辑或歌单" aria-label="检索我的音乐"></label><button data-library="${this.cloudAccount?'cloud-create':'netease-qr'}">${this.cloudAccount?'＋ 创建歌单':'绑定网易云'}</button></div>
      <div class="cloud-tabs">${[...(this.cloudAccount?[['liked','喜欢的歌曲'],['created','我的歌单'],['saved','收藏歌单'],['albums','收藏专辑']]:[]),...(this.cloudServer?.length?[['server','服务器歌单']]:[])].map(([id,label])=>`<button data-library="cloud-tab" data-tab="${id}" aria-selected="${tab===id}">${label}</button>`).join('')}<div class="cloud-sort">${tab==='liked'?[['','原序'],['artist','歌手分组'],['album','专辑分组']].map(([id,label])=>`<button data-library="cloud-sort" data-sort="${id}" aria-selected="${(this.cloudSort||'')===id}">${label}</button>`).join(''):''}</div></div>
      <div class="cloud-columns"><span>ID / 编号</span><span>TITLE / 音乐</span><span>OPEN</span></div>
      <div class="cloud-directory-rows">${this.cloudError?`<p role="status">${esc(this.cloudError)}</p><button data-library="cloud-tab" data-tab="${tab}">重试 ↻</button>`:rows.map((r,i)=>`<button class="cloud-row" data-library="cloud-open" data-index="${i}"><small>${esc(r.id)}</small><span><strong>${esc(r.title||r.name)}</strong><small><i>${tab==='server'?'SERVER':tab==='albums'?'ALBUM':tab==='liked'?'SONG':'PLAYLIST'}</i> ${esc(r.artist||((r.trackCount||0)+' 首'))}</small></span><small>查看 ↗</small></button>`).join('')||'<p class="list-message">暂无匹配内容。</p>'}</div>
      <div class="cloud-directory-footer"><button data-library="cloud-manage">歌单管理 ↗</button><span>已载入 ${rows.length}${tab==='liked'?' / '+(this.cloudTotal||0):''}${tab==='albums'&&this.cloudAlbumMore?' <button data-library="cloud-albums-more">加载更多 ↓</button>':''}${tab==='liked'&&(this.cloudOffset||0)<this.cloudTotal?' <button data-library="cloud-more">加载更多 ↓</button>':''}</span></div>`;
    const input=$("cloudDirectoryQuery");
    input.addEventListener('input',()=>{const pos=input.selectionStart;this.cloudQuery=input.value;this.drawCloudDirectory();$("cloudDirectoryQuery").focus();$("cloudDirectoryQuery").setSelectionRange(pos,pos);});
  }
  async cloudMore(button) {
    const token=this.neteaseRequest,liked=this.neteasePlaylists.find(p=>p.liked);if(!liked)return;
    button.disabled=true;
    try {
      const data=await libraryApi('/netease/playlists/'+encodeURIComponent(liked.id)+'/tracks?offset='+this.cloudOffset+'&limit=100');
      if(token!==this.neteaseRequest||this.hooks.currentDialog()!=='netease')return;
      this.cloudSongs.push(...data.tracks.filter(Boolean).map(normalizeCloud));this.cloudOffset+=data.tracks.length;this.cloudTotal=data.tracks.length?data.total:this.cloudOffset;this.drawCloudDirectory();
    } finally {button.disabled=false;}
  }
  showCloudManagement() {
    this.neteaseRequest++;
    $("dialogPanel").innerHTML=this.hooks.panelHeader('网易云歌单管理','NETEASE / MANAGE')+`<button data-library="netease">← 返回我的音乐</button><div class="library-toolbar"><button data-library="netease-sync">同步我的歌单</button><button data-library="netease-unbind">解除绑定</button><button data-library="account">账号设置 ↗</button></div>${!this.cloudAccount?'<button data-library="netease-qr">扫码绑定网易云</button>':''}${this.cloudManagement}`;
  }
  async startNeteaseQr() {
    const token = ++this.qrToken;
    this.hooks.showDialog("import");
    $("dialogPanel").innerHTML = this.hooks.panelHeader("绑定网易云") + '<p class="list-message">正在生成二维码…</p>';
    const qr = await libraryApi("/netease/qr/start", {});
    if (token !== this.qrToken) return;
    $("dialogPanel").innerHTML = this.hooks.panelHeader("绑定网易云") +
      `<button class="text-back" data-library="netease">← 返回</button><p class="setting-description">请用网易云音乐 App 扫码并确认。二维码约 3 分钟后过期；不要把账号密码或 Cookie 发给本站。</p><img src="${esc(qr.image)}" alt="网易云音乐绑定二维码" style="display:block;width:min(68vw,240px);height:auto;margin:1.5rem auto"><p id="neteaseQrStatus" class="setting-description" role="status">等待扫码…</p>`;
    for (let i = 0; i < 50 && token === this.qrToken; i++) {
      await new Promise((resolve) => setTimeout(resolve, 3500));
      if (token !== this.qrToken || this.hooks.currentDialog() !== "import") return;
      try {
        const result = await libraryApi("/netease/qr/status", {});
        if (token !== this.qrToken) return;
        if (result.code === 803) {
          $("neteaseQrStatus").textContent = "绑定成功，正在同步歌单…";
          await libraryApi("/netease/sync", {});
          await this.renderNetease();
          return;
        }
        $("neteaseQrStatus").textContent = result.message || "等待扫码…";
        if (result.code === 800) return;
      } catch (error) {
        if (error.status === 429) continue;
        if (token === this.qrToken) $("neteaseQrStatus").textContent = error.message;
        return;
      }
    }
  }
  async renderNeteasePlaylist(id) {
    const list = this.neteasePlaylists?.find((p) => String(p.id) === String(id));
    const request = ++this.neteaseRequest;
    this.neteasePlaylistId = id;
    this.neteaseTitle = list?.name || "网易云歌单";
    this.neteaseTracks = [];
    this.neteaseTotal = Number(list?.trackCount) || 0;
    this.neteasePage = 0;
    this.hooks.showDialog("import");
    $("dialogPanel").innerHTML = this.hooks.panelHeader(list?.name || "网易云歌单") + '<p class="list-message">正在读取歌曲…</p>';
    await this.loadNeteasePage(0, request);
  }
  async loadNeteasePage(page, request = ++this.neteaseRequest) {
    const offset = page * 100;
    if (!this.neteaseTracks.slice(offset, offset + 100).some(Boolean)) {
      $("dialogPanel").innerHTML = this.hooks.panelHeader(this.neteaseTitle) + '<p class="list-message">正在读取第 ' + (page + 1) + ' 页…</p>';
      try {
        const data = await libraryApi("/netease/playlists/" + encodeURIComponent(this.neteasePlaylistId) + "/tracks?offset=" + offset + "&limit=100");
        if (request !== this.neteaseRequest) return;
        this.neteaseTotal = data.total;
        data.tracks.forEach((song, index) => { this.neteaseTracks[offset + index] = song ? normalizeCloud(song) : null; });
      } catch (error) {
        if (request === this.neteaseRequest) $("dialogPanel").innerHTML = this.hooks.panelHeader(this.neteaseTitle) + '<p class="list-message">' + esc(error.message) + '</p><button class="solid-button" data-library="netease-page" data-page="' + page + '">重试</button>';
        return;
      }
    }
    if (request !== this.neteaseRequest) return;
    this.neteasePage = page;
    this.drawNeteaseTracks();
  }
  drawNeteaseTracks() {
    const tracks = this.neteaseTracks;
    const start = this.neteasePage * 100;
    const visible = tracks.slice(start, start + 100);
    const playable = visible.filter(Boolean);
    $("dialogPanel").innerHTML = this.hooks.panelHeader(this.neteaseTitle, `NETEASE / ${this.neteaseTotal} TRACKS`) +
      `<button class="text-back" data-library="netease">← 返回网易云歌单</button><div class="library-toolbar"><button class="solid-button primary" data-library="netease-play-all" ${playable.length ? "" : "disabled"}>▶ 播放本页</button><button class="solid-button" data-library="netease-browse" ${playable.length ? "" : "disabled"}>查看本页 3D 资料库</button></div><ol class="song-list">${visible.map((t, i) => t ? `<li class="song-row"><span class="index">${String(start + i + 1).padStart(2, "0")}</span><button class="song-main" data-library="netease-play-song" data-index="${start + i}"><strong>${esc(t.title)}</strong><small>${esc(t.artist)} / ${esc(t.album)}</small></button></li>` : "").join("")}</ol><div class="library-toolbar"><button class="solid-button" data-library="netease-page" data-page="${this.neteasePage - 1}" ${start === 0 ? "disabled" : ""}>上一页</button><span>${this.neteasePage + 1} / ${Math.max(1, Math.ceil(this.neteaseTotal / 100))}</span><button class="solid-button" data-library="netease-page" data-page="${this.neteasePage + 1}" ${start + 100 >= this.neteaseTotal ? "disabled" : ""}>下一页</button></div>`;
  }
  async renderUsers() {
    this.account = (await libraryApi("/session")).account;
    if (!this.account?.admin) return this.renderAccount();
    const { users } = await libraryApi("/users");
    this.hooks.showDialog("import");
    $("dialogPanel").innerHTML = this.hooks.panelHeader("账号授权", "ADMIN / WHITELIST") +
      `<p class="setting-description">用户自行注册。听歌权限可以播放服务器专辑；上传与维护权限还允许修改服务器专辑、曲目信息。点选后立即生效。</p><button class="solid-button" data-library="account">← 返回账号</button><div class="library-groups">${users.map((u) => `<div class="user-permission-row"><strong>${esc(u.qq)}${u.admin ? " · 管理员" : ""}</strong><small>${u.canListen ? "可听歌" : "待审核"} · ${u.canUpload ? "可上传与维护" : "不可上传"}</small>${u.admin ? "" : `<div><button class="solid-button" data-library="grant" data-id="${u.id}" data-field="listen" data-listen="${u.listenGranted}" data-upload="${u.uploadGranted}">${u.listenGranted ? "撤销单独听歌授权" : "单独开通听歌"}</button><button class="solid-button" data-library="grant" data-id="${u.id}" data-field="upload" data-listen="${u.listenGranted}" data-upload="${u.uploadGranted}">${u.uploadGranted ? "取消上传与维护" : "开通上传与维护"}</button></div>`}</div>`).join("")}</div>`;
  }
  async pick(folder) {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    if (folder) input.webkitdirectory = true;
    else
      input.accept =
        ".mp3,.flac,.m4a,.aac,.ogg,.wav,.lrc,.png,.jpg,.jpeg,.webp,.txt";
    input.onchange = async () => {
      if (!input.files.length) return;
      this.busy = true;
      this.progress = null;
      this.transfer = null;
      this.renderImport();
      try {
        this.preview = await analyzeFiles(input.files, (i, n, name) => {
          this.analysisMessage = `正在识别 ${i} / ${n}：${name}`;
          this.renderImport();
        });
      } catch (e) {
        this.hooks.toast(e.message);
      } finally {
        this.busy = false;
        this.renderImport();
      }
    };
    input.click();
  }
  async begin() {
    if (!this.preview) return;
    for (const t of this.preview.tracks)
      if (!t.title.trim() || !t.artist.trim() || !t.album.trim())
        throw new Error("请补齐曲名、歌手和专辑名");
    if (!this.transfer)
      this.transfer = new ImportTransfer(this.preview, (progress) => {
        this.progress = { ...this.progress, ...progress };
        this.renderImport();
      });
    this.progress = { total: this.preview.totalBytes, uploaded: 0 };
    this.renderImport();
    try {
      const result = await this.transfer.start();
      if (result) {
        await this.hooks.reloadTracks();
        await this.refresh();
      }
    } catch (e) {
      this.hooks.toast(e.message);
    }
  }
  reset() {
    this.transfer?.pause();
    this.transfer = null;
    this.preview = null;
    this.progress = null;
    for (const url of this.objectUrls?.values() || []) URL.revokeObjectURL(url);
    this.objectUrls = new Map();
    this.renderImport();
  }
  async submit(form) {
    if (form.id === "cloudCreate") {
      const button=form.querySelector('button[type=submit]'); if(button.disabled)return;
      button.disabled=true;
      try { await libraryApi('/netease/playlists', {name:form.elements.name.value,private:form.elements.private.checked});
        this.cloudTab='created'; if(form.isConnected)await this.renderNetease(); this.hooks.toast('已在网易云创建歌单');
      } catch(e) { form.querySelector('[role=alert]').textContent=e.message; }
      finally { button.disabled=false; }
      return;
    }
    if (form.id === "neteasePublicImport") {
      try {
        const id = playlistId(new FormData(form).get("playlist"));
        if (!id) throw new Error("请填写网易云公开歌单链接或编号");
        await libraryApi("/netease/import-public", { id });
        await this.renderNetease();
        this.hooks.toast("公开歌单已导入");
      } catch (error) {
        const target = $("neteaseImportError");
        if (target) target.textContent = error.message;
      }
      return;
    }
    if (["libraryLogin", "libraryRegister", "ownerClaim"].includes(form.id)) {
      try {
        const data = Object.fromEntries(new FormData(form));
        const route = form.id === "ownerClaim" ? "/owner/claim" : form.id === "libraryRegister" ? "/register" : "/login";
        const result = await libraryApi(route, data);
        this.account = result.account;
        if (this.account?.canListen) {
          await this.hooks.reloadTracks();
          if (this.returnTo === "import" && this.account.canUpload) this.renderImport();
          else await this.show();
        } else this.renderAccount();
      } catch (e) {
        $("loginError").textContent = e.message;
      }
      return;
    }
    if (form.id === "albumEdit") {
      const data = Object.fromEntries(new FormData(form));
      await libraryApi("/albums/" + this.activeAlbum.id, data, "PATCH");
      await this.hooks.reloadTracks();
      await this.refresh();
      this.album(this.activeAlbum.id);
      this.hooks.toast("专辑信息已更新");
    }
    if (form.id === "trackEdit") {
      const data = Object.fromEntries(new FormData(form));
      await libraryApi("/tracks/" + this.activeTrack.id, data, "PATCH");
      await this.hooks.reloadTracks();
      await this.refresh();
      this.album(this.activeTrack.albumId);
      this.hooks.toast("曲目信息已更新");
    }
  }
  async action(el) {
    const action = el.dataset.library;
    if (action === "view") {
      this.view = el.dataset.value;
      this.render();
    }
    if (action === "catalog") await this.show();
    if (action === "refresh") {
      await this.hooks.reloadTracks();
      await this.refresh();
      this.render();
    }
    if (action === "import") await this.openImport();
    if (action === "account") {
      this.qrToken++;
      this.account = (await libraryApi("/session")).account;
      this.renderAccount();
    }
    if (action === "netease") await this.renderNetease();
    if (action === "cloud-tab") {this.cloudTab=el.dataset.tab;this.cloudQuery='';await this.drawCloudHome(true);}
    if (action === "cloud-create") {
      this.neteaseRequest++;
      $("dialogPanel").innerHTML=this.hooks.panelHeader('创建网易云歌单','NETEASE / NEW PLAYLIST')+'<button data-library="netease">← 返回我的音乐</button><form id="cloudCreate" class="manager-form"><label>歌单名称<input name="name" required maxlength="40" autocomplete="off"></label><label><input type="checkbox" name="private"> 私密歌单</label><button type="submit" class="solid-button primary">创建歌单</button><p role="alert"></p></form>';
    }
    if (action === "cloud-albums-more") {
      const token=this.neteaseRequest; el.disabled=true;
      try { const data=await libraryApi('/netease/albums?offset='+this.cloudAlbumOffset);
        if(token!==this.neteaseRequest||this.hooks.currentDialog()!=='netease')return;
        this.cloudAlbums.push(...data.albums); this.cloudAlbumOffset+=data.albums.length; this.cloudAlbumMore=data.more&&data.albums.length>0; this.drawCloudDirectory();
      } finally {el.disabled=false;}
    }
    if (action === "cloud-sort") {this.cloudSort=el.dataset.sort;this.drawCloudDirectory();}
    if (action === "cloud-manage") this.showCloudManagement();
    if (action === "cloud-more") await this.cloudMore(el);
    if (action === "cloud-open") {
      const row=this.cloudVisible[Number(el.dataset.index)];if(!row)return;
      if(this.cloudTab==='liked') { this.hooks.useCollection(this.cloudVisible,'cloud','喜欢的歌曲'); this.hooks.select(Number(el.dataset.index)); this.hooks.openDetail(); }
      else if(this.cloudTab==='server') this.hooks.useCollection(row.tracks||[], 'local', row.title);
      else if(this.cloudTab==='albums') {
        const token=++this.neteaseRequest; el.disabled=true;
        try { const data=await libraryApi('/netease/albums/'+encodeURIComponent(row.id)+'/tracks');
          if(token===this.neteaseRequest&&this.hooks.currentDialog()==='netease')this.hooks.useCollection(data.tracks.map(normalizeCloud),'cloud',row.name);
        } finally {el.disabled=false;}
      }
      else await this.renderNeteasePlaylist(String(row.id));
    }
    if (action === "netease-qr") await this.startNeteaseQr();
    if (action === "netease-sync") {
      this.hooks.toast("正在同步网易云歌单…");
      await libraryApi("/netease/sync", {});
      await this.renderNetease();
    }
    if (action === "netease-unbind") {
      await libraryApi("/netease/account", undefined, "DELETE");
      await this.renderNetease();
    }
    if (action === "netease-list") await this.renderNeteasePlaylist(el.dataset.id);
    if (action === "netease-page") {
      await this.loadNeteasePage(Number(el.dataset.page));
    }
    if (action === "netease-play-song")
      this.hooks.play(this.neteaseTracks[Number(el.dataset.index)], this.neteaseTracks.slice(this.neteasePage * 100, this.neteasePage * 100 + 100).filter(Boolean));
    if (action === "netease-play-all" && this.neteaseTracks.length) {
      const page = this.neteaseTracks.slice(this.neteasePage * 100, this.neteasePage * 100 + 100).filter(Boolean);
      if (page.length) this.hooks.play(page[0], page);
    }
    if (action === "netease-browse" && this.neteaseTracks.length)
      this.hooks.useCollection(this.neteaseTracks.slice(this.neteasePage * 100, this.neteasePage * 100 + 100).filter(Boolean), "cloud", this.neteaseTitle);
    if (action === "auth-mode") {
      this.authMode = el.dataset.value;
      this.renderAccount();
    }
    if (action === "users") await this.renderUsers();
    if (action === "grant") {
      const listen = el.dataset.listen === "true";
      const upload = el.dataset.upload === "true";
      const next = el.dataset.field === "listen"
        ? { canListen: !listen, canUpload: upload }
        : { canListen: listen, canUpload: !upload };
      await libraryApi("/users/" + el.dataset.id, next, "PATCH");
      await this.renderUsers();
    }
    if (action === "all") {
      this.hooks.useCollection(this.hooks.allTracks(), "local", "我的曲库");
    }
    if (action === "album") this.album(el.dataset.id);
    if (action === "artist")
      this.hooks.useCollection(
        this.hooks.allTracks().filter((t) => t.artist === el.dataset.value),
        "local",
        el.dataset.value,
      );
    if (action === "folder")
      this.hooks.useCollection(
        this.catalog.tracks.filter(
          (t) => directoryOf(t.relativePath) === el.dataset.value,
        ),
        "local",
        el.dataset.value.split("/").pop(),
      );
    if (action === "play-track" || action === "detail-track") {
      const track = this.hooks
        .allTracks()
        .find((t) => t.key === el.dataset.key);
      if (!track) return;
      const list =
        this.activeAlbum?.tracks.some((t) => t.key === track.key) &&
        this.hooks.currentDialog() === "album"
          ? this.activeAlbum.tracks
          : this.hooks.allTracks();
      if (action === "play-track") this.hooks.play(track, list);
      else {
        this.hooks.useCollection(list, "local", track.album);
        this.hooks.select(list.findIndex((t) => t.key === track.key));
        this.hooks.openDetail();
      }
    }
    if (action === "play-album")
      this.hooks.play(this.activeAlbum.tracks[0], this.activeAlbum.tracks);
    if (action === "browse-album")
      this.hooks.useCollection(
        this.activeAlbum.tracks,
        "local",
        this.activeAlbum.title,
      );
    if (action === "pick-folder") await this.pick(true);
    if (action === "pick-files") await this.pick(false);
    if (action === "start-import" || action === "resume") await this.begin();
    if (action === "pause") {
      this.transfer?.pause();
      this.progress = { ...this.progress, paused: true };
      this.renderImport();
    }
    if (action === "reset-import") this.reset();
    if (action === "logout") {
      this.transfer?.pause();
      await libraryApi("/logout", {});
      this.account = null;
      this.catalog = { albums: [], tracks: [] };
      await this.hooks.reloadTracks();
      this.renderAccount();
    }
    if (action === "edit-album") {
      this.account = (await libraryApi("/session")).account;
      if (!this.account?.canUpload) {
        this.hooks.toast("需要上传与维护权限");
        return;
      }
      const a = this.activeAlbum;
      $("dialogPanel").innerHTML =
        this.hooks.panelHeader("编辑专辑") +
        `<form id="albumEdit" class="manager-form"><label>专辑名称<input name="title" required maxlength="250" value="${esc(a.title)}"></label><label>专辑艺术家<input name="artist" required maxlength="250" value="${esc(a.artist)}"></label><label>年份<input name="year" maxlength="20" value="${esc(a.year)}"></label><button class="solid-button primary" type="submit">保存信息</button></form>`;
    }
    if (action === "edit-track") {
      this.account = (await libraryApi("/session")).account;
      if (!this.account?.canUpload) throw new Error("需要上传与维护权限");
      const track = this.catalog.tracks.find((t) => t.id === el.dataset.id);
      if (!track) throw new Error("曲目不存在，请刷新曲库");
      this.activeTrack = track;
      this.hooks.showDialog("import");
      $("dialogPanel").innerHTML = this.hooks.panelHeader("编辑曲目信息") +
        `<form id="trackEdit" class="manager-form"><label>曲名<input name="title" required maxlength="250" value="${esc(track.title)}"></label><label>歌手<input name="artist" maxlength="250" value="${esc(track.artist)}"></label><label>碟号<input name="discNumber" type="number" min="1" max="99" required value="${track.discNumber}"></label><label>曲号<input name="trackNumber" type="number" min="1" max="9999" required value="${track.trackNumber}"></label><label>制作信息<input name="credits" maxlength="2000" value="${esc(track.credits || "")}"></label><button class="solid-button primary" type="submit">保存曲目</button></form>`;
    }
    if (action === "delete-album") {
      if (!confirm(`确定删除专辑「${el.dataset.title}」及其全部曲目吗？此操作可由管理员恢复。`)) return;
      await libraryApi("/albums/" + el.dataset.id, undefined, "DELETE");
      this.hooks.toast("专辑已删除");
      await this.hooks.reloadTracks();
      await this.refresh();
      this.render();
    }
    if (action === "delete-track") {
      if (!confirm(`确定删除曲目「${el.dataset.title}」吗？此操作可由管理员恢复。`)) return;
      await libraryApi("/tracks/" + el.dataset.id, undefined, "DELETE");
      this.hooks.toast("曲目已删除");
      await this.hooks.reloadTracks();
      await this.refresh();
      if (this.activeAlbum) this.album(this.activeAlbum.id);
      else this.render();
    }
    if (action === "delete-import") {
      if (!confirm("确定删除此导入产生的全部专辑和曲目吗？此操作可由管理员恢复。")) return;
      await libraryApi("/imports/" + el.dataset.id, undefined, "DELETE");
      this.hooks.toast("导入已删除");
      this.imports = null;
      await this.hooks.reloadTracks();
      await this.refresh();
      await this.loadImports();
      this.render();
    }
  }
}
const directoryOf = (path) =>
  path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "根目录";
